import React, { useState, useEffect, useMemo } from 'react';
import { Form, Button, Table, Alert, Spinner, InputGroup, Row, Col } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { getDataList, postDataEquipos } from '../../../api/common';
import ModalCorregirEquipos from './ModalCorregirEquipos';

/**
 * EquipoExistente - Component to select existing equipos
 * @param {Object} props - Component props
 * @param {string} props.clienteId - ID of selected cliente
 * @param {Array} props.equiposYaAgregados - Array of equipos already added to the OT
 * @param {Function} props.onAgregar - Handler receiving array of selected equipos
 * @param {number} props.refreshTrigger - Trigger to refresh the equipos list
 * @param {Array} props.listaSedes - Lista de sedes del cliente para el modal de corrección
 * @param {Function} props.onCorregir - Handler para corregir equipos con datos incompletos
 * @returns {JSX.Element}
 */
const EquipoExistente = ({ clienteId, equiposYaAgregados = [], onAgregar, onResolverDependientes, refreshTrigger = 0, listaSedes = [], onCorregir, initialEquipos = null, onEquiposLoaded = null }) => {
        

    const [equipos, setEquipos] = useState([]);
    const [filteredEquipos, setFilteredEquipos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedEquipos, setSelectedEquipos] = useState([]);
    
    // Estados para filtros
    const [filtroItem, setFiltroItem] = useState('');
    const [filtroSede, setFiltroSede] = useState('');

    // Estados para modal de corrección
    const [showModalCorregir, setShowModalCorregir] = useState(false);
    const [equiposProblematicos, setEquiposProblematicos] = useState([]);
    // Cache de equipos corregidos para evitar pérdida de datos
    const [equiposCorregidosCache, setEquiposCorregidosCache] = useState(new Map());
    
    // Set de IDs de equipos que SON padre (referenciados por otro equipo vía equipoDependiente).
    // Opción A: derivación client-side sobre la lista ya cargada — cero llamada extra.
    // Spec items-adicionales-equipo-dependiente §Requirement badge en tabla.
    const idsPadres = useMemo(() => {
        const s = new Set();
        for (const eq of equipos) {
            if (eq.equipoDependiente) {
                const padreId = typeof eq.equipoDependiente === 'string' ? eq.equipoDependiente : eq.equipoDependiente._id;
                if (padreId) s.add(String(padreId));
            }
        }
        return s;
    }, [equipos]);

    // Extraer lista única de Items ordenada alfabéticamente
    const listaItems = useMemo(() => {
        const itemsUnicos = new Map();
        equipos.forEach(equipo => {
            if (equipo.item && equipo.item._id) {
                itemsUnicos.set(equipo.item._id, equipo.item.nombre);
            }
        });
        return Array.from(itemsUnicos.entries())
            .map(([id, nombre]) => ({ id, nombre }))
            .sort((a, b) => a.nombre.localeCompare(b.nombre));
    }, [equipos]);

    // Extraer lista única de Sedes presentes en equipos (para filtro)
    const sedesEnEquipos = useMemo(() => {
        const sedesUnicas = new Map();
        equipos.forEach(equipo => {
            if (equipo.sedeClient && equipo.sedeClient._id) {
                sedesUnicas.set(equipo.sedeClient._id, equipo.sedeClient.nameSede);
            }
        });
        return Array.from(sedesUnicas.entries())
            .map(([id, nombre]) => ({ id, nombre }))
            .sort((a, b) => a.nombre.localeCompare(b.nombre));
    }, [equipos]);

    // Load equipos when cliente changes
    useEffect(() => {
        if (clienteId) {
            if (initialEquipos && initialEquipos.length > 0) {
                // Use cached data from parent — skip network request
                setEquipos(initialEquipos);
                setFilteredEquipos(initialEquipos);
                setSelectedEquipos([]);
            } else {
                loadEquipos();
            }
        } else {
            setEquipos([]);
            setFilteredEquipos([]);
            setSelectedEquipos([]);
            limpiarFiltros();
        }
    }, [clienteId]);

    // Reload equipos when refreshTrigger changes (new equipos created)
    useEffect(() => {
        if (refreshTrigger > 0 && clienteId) {
            loadEquipos();
        }
    }, [refreshTrigger]);

    // Aplicar filtros combinados (Item AND Sede AND searchTerm)
    useEffect(() => {
        let filtered = [...equipos];

        // Filtro por Item
        if (filtroItem) {
            filtered = filtered.filter(equipo => equipo.item?._id === filtroItem);
        }

        // Filtro por Sede
        if (filtroSede) {
            filtered = filtered.filter(equipo => equipo.sedeClient?._id === filtroSede);
        }

        // Filtro por búsqueda de texto
        if (searchTerm.trim() !== '') {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(equipo => 
                (equipo.serie || '').toLowerCase().includes(term) ||
                (equipo.marca || '').toLowerCase().includes(term) ||
                (equipo.modelo || '').toLowerCase().includes(term) ||
                (equipo.item?.nombre || '').toLowerCase().includes(term)
            );
        }

        setFilteredEquipos(filtered);
    }, [searchTerm, filtroItem, filtroSede, equipos]);

    const loadEquipos = async () => {

        setLoading(true);
        setError(null);
        try {
            const response = await getDataList('Equipos',{ cliente: clienteId });

            const equiposData = response.data.data || [];
            setEquipos(equiposData);
            setFilteredEquipos(equiposData);
            setSelectedEquipos([]);
            onEquiposLoaded?.(equiposData); // Notificar al padre para cachear
            return equiposData; // Retornar para uso en otros handlers
        } catch (err) {
            setError('Error al cargar equipos: ' + (err.response?.data?.message || err.message));
            return [];
        } finally {
            setLoading(false);
        }
    };

    const limpiarFiltros = () => {
        setFiltroItem('');
        setFiltroSede('');
        setSearchTerm('');
    };

    const handleToggle = (equipo) => {
        setSelectedEquipos(prev => {
            const exists = prev.find(e => e._id === equipo._id);
            if (exists) {
                return prev.filter(e => e._id !== equipo._id);
            }
            return [...prev, equipo];
        });
    };

    const handleToggleAll = (checked) => {
        if (checked) {
            // Seleccionar todos los equipos filtrados que no estén ya agregados
            const equiposSeleccionables = filteredEquipos.filter(eq => 
                !isAlreadyAdded(eq._id)
            );
            
            // Mantener equipos ya seleccionados que no están en el filtro actual
            const equiposNoFiltrados = selectedEquipos.filter(eq => 
                !filteredEquipos.some(f => f._id === eq._id)
            );
            
            setSelectedEquipos([...equiposNoFiltrados, ...equiposSeleccionables]);
        } else {
            // Deseleccionar solo los equipos del filtro actual
            setSelectedEquipos(prev => 
                prev.filter(eq => !filteredEquipos.some(f => f._id === eq._id))
            );
        }
    };

    // Función para detectar equipos sin sede completa
    const detectarEquiposSinSede = (equiposARevisar) => {
        return equiposARevisar.filter(eq => !eq.sedeClient || !eq.sedeClient._id || !eq.sedeClient.nameSede);
    };

    const handleAgregar = async () => {
        if (selectedEquipos.length === 0) return;

        // Filter out equipos that are already added
        const equiposYaAgregadosIds = equiposYaAgregados.map(e => e._id);
        const equiposNuevos = selectedEquipos.filter(e => !equiposYaAgregadosIds.includes(e._id));

        if (equiposNuevos.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Equipos ya agregados',
                text: 'Todos los equipos seleccionados ya están en la lista'
            });
            return;
        }

        if (equiposNuevos.length < selectedEquipos.length) {
            const duplicados = selectedEquipos.length - equiposNuevos.length;
            Swal.fire({
                icon: 'info',
                title: 'Algunos equipos ya agregados',
                text: `${equiposNuevos.length} equipos agregados. ${duplicados} ya estaban en la lista.`
            });
        }

        // Resolución de dependientes ANTES de detectarEquiposSinSede
        // (spec items-adicionales-equipo-dependiente §Requirement inclusión OT).
        // Si el usuario cancela el modal, preservamos selectedEquipos para que pueda ajustar.
        let equiposParaSede = equiposNuevos;
        if (typeof onResolverDependientes === 'function') {
            const idsUnion = [...equiposYaAgregadosIds, ...equiposNuevos.map(e => e._id)];
            const resolverRes = await onResolverDependientes(idsUnion);
            if (resolverRes?.cancelado) {
                return; // preserva selectedEquipos
            }
            if (resolverRes?.incluirTodos && Array.isArray(resolverRes.extra) && resolverRes.extra.length > 0) {
                // Añadir hijos/padres omitidos que aún no estén en la OT.
                const yaEstoyIds = new Set([...equiposYaAgregadosIds, ...equiposNuevos.map(e => e._id)].map(String));
                for (const ex of resolverRes.extra) {
                    if (!yaEstoyIds.has(String(ex._id))) {
                        equiposParaSede = [...equiposParaSede, ex];
                    }
                }
            }
            // "Solo estos equipos": el warning se maneja al momento de crearOt en el hook.
        }

        // DETECCIÓN PREVENTIVA: Verificar si hay equipos sin sede antes de agregar
        const problematicos = detectarEquiposSinSede(equiposParaSede);

        if (problematicos.length > 0) {
            setEquiposProblematicos(problematicos);
            setShowModalCorregir(true);
            return; // No agregar hasta que se corrijan
        }
        // AGREGAR equipos nuevos si no hay problemas de sede
        if (equiposParaSede.length > 0) {
            onAgregar(equiposParaSede);
        }
        setSelectedEquipos([]);
    };

    // Handler para cuando se cierra el modal después de corregir todos
    const handleModalClose = async () => {
        setShowModalCorregir(false);
        
        // Obtener IDs de equipos seleccionados
        const idsSeleccionados = selectedEquipos.map(e => e._id);
        
        // Construir lista de equipos APLICANDO correcciones del cache
        const equiposParaAgregar = selectedEquipos.map(equipo => {
            const correccion = equiposCorregidosCache.get(equipo._id);
            
            if (correccion) {
                console.log('✅ Aplicando corrección del cache a:', equipo._id);
                // Retornar equipo con correcciones aplicadas (tiene todos los campos)
                return correccion;
            } else {
                console.log('⚠️ Equipo sin corrección en cache:', equipo._id);
                // Retornar equipo original (no debería pasar si todos se corrigieron)
                return equipo;
            }
        });
        
        console.log('📋 Equipos a agregar con correcciones:', equiposParaAgregar.map(e => ({ 
            id: e._id,
            item: e.item?.nombre,
            tieneSedeId: !!e.sedeClient?._id,
            tieneSedeName: !!e.sedeClient?.nameSede,
            sede: e.sedeClient?.nameSede
        })));
        
        // Filtrar los que no están ya agregados a la OT
        const equiposYaAgregadosIds = equiposYaAgregados.map(e => e._id);
        const equiposNuevos = equiposParaAgregar.filter(e => !equiposYaAgregadosIds.includes(e._id));
        
        if (equiposNuevos.length > 0) {
            console.log('✅ EquipoExistente: Agregando', equiposNuevos.length, 'equipos CON correcciones aplicadas');
            onAgregar(equiposNuevos);
        } else {
            console.log('⚠️ EquipoExistente: No hay equipos nuevos para agregar');
        }
        
        // Limpiar estado
        setSelectedEquipos([]);
        setEquiposCorregidosCache(new Map());  // Limpiar cache después de usar
        
        // Recargar lista de equipos para reflejar cambios en BD
        console.log('🔄 EquipoExistente: Recargando lista de equipos disponibles...');
        await loadEquipos();
    };

    // Handler para cuando se cancela el modal sin corregir
    const handleModalCancelar = () => {

        setShowModalCorregir(false);
        
        // Limpiar estado sin agregar equipos
        setSelectedEquipos([]);
        setEquiposCorregidosCache(new Map());
        
        console.log('⚠️ EquipoExistente: Equipos NO agregados (cancelado)');
    };

    const isAlreadyAdded = (equipoId) => {
        return equiposYaAgregados.some(e => e._id === equipoId);
    };

    const isSelected = (equipoId) => {
        return selectedEquipos.some(e => e._id === equipoId);
    };

    // Check All está marcado si TODOS los equipos filtrados (que no están ya agregados) están seleccionados
    const equiposSeleccionables = filteredEquipos.filter(eq => !isAlreadyAdded(eq._id));
    const allSelected = equiposSeleccionables.length > 0 && 
        equiposSeleccionables.every(eq => isSelected(eq._id));

    // Contar equipos seleccionados del filtro actual
    const seleccionadosEnFiltro = filteredEquipos.filter(eq => isSelected(eq._id)).length;

    return (
        <div>
            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}

            {!clienteId ? (
                <Alert variant="warning">
                    Debe seleccionar un cliente para ver sus equipos existentes
                </Alert>
            ) : (
                <>
                    {/* Fila de Filtros */}
                    <Row className="mb-3">
                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Filtrar por Item</Form.Label>
                                <Form.Select 
                                    value={filtroItem} 
                                    onChange={(e) => setFiltroItem(e.target.value)}
                                    disabled={loading}
                                >
                                    <option value="">Todos los items</option>
                                    {listaItems.map(item => (
                                        <option key={item.id} value={item.id}>
                                            {item.nombre}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={3}>
                            <Form.Group>
                                <Form.Label>Filtrar por Sede</Form.Label>
                                <Form.Select 
                                    value={filtroSede} 
                                    onChange={(e) => setFiltroSede(e.target.value)}
                                    disabled={loading || !clienteId}
                                >
                                    <option value="">Todas las sedes</option>
                                    {sedesEnEquipos.map(sede => (
                                        <option key={sede.id} value={sede.id}>
                                            {sede.nombre}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>

                        <Col md={5}>
                            <Form.Group>
                                <Form.Label>Buscar</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>🔍</InputGroup.Text>
                                    <Form.Control
                                        type="text"
                                        placeholder="Serie, marca, modelo..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        disabled={loading}
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>

                        <Col md={1} className="d-flex align-items-end">
                            <Button 
                                variant="outline-secondary" 
                                onClick={limpiarFiltros}
                                disabled={loading || (!filtroItem && !filtroSede && !searchTerm)}
                                title="Limpiar filtros"
                                style={{ marginBottom: '0px' }}
                            >
                                <i className="bi bi-x-circle"></i>
                            </Button>
                        </Col>
                    </Row>

                    {loading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" />
                            <p className="mt-2">Cargando equipos...</p>
                        </div>
                    ) : filteredEquipos.length === 0 ? (
                        <Alert variant="info">
                            {equipos.length === 0 
                                ? 'No hay equipos registrados para este cliente'
                                : 'No se encontraron equipos con los criterios de filtrado'
                            }
                        </Alert>
                    ) : (
                        <>
                            <div className="mb-2 d-flex justify-content-between align-items-center">
                                <small className="text-muted">
                                    {selectedEquipos.length} seleccionado(s) en total | {seleccionadosEnFiltro} de {filteredEquipos.length} en filtro actual
                                </small>
                                {(filtroItem || filtroSede || searchTerm) && (
                                    <small className="text-primary">
                                        <i className="bi bi-funnel-fill"></i> Filtros activos
                                    </small>
                                )}
                            </div>

                            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                <Table striped bordered hover>
                                    <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                                        <tr>
                                            <th>#</th>
                                            <th style={{ width: '60px', textAlign: 'center' }}>
                                                <input type="checkbox"
                                                    checked={allSelected}
                                                    onChange={(e) => handleToggleAll(e.target.checked)}
                                                    disabled={equiposSeleccionables.length === 0}
                                                    style={{ transform: 'scale(1.3)' }}
                                                    title={allSelected ? 'Deseleccionar todos (filtrados)' : 'Seleccionar todos (filtrados)'}
                                                 />
                                            </th>
                                            <th>Item</th>
                                            <th>Marca</th>
                                            <th>Modelo</th>
                                            <th>Serie</th>
                                            <th>Inventario</th>
                                            <th>Sede</th>
                                            <th>Ubicación</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredEquipos.map((equipo, index) => {
                                            const yaAgregado = isAlreadyAdded(equipo._id);
                                            return (
                                                <tr 
                                                    key={equipo._id}
                                                    onClick={() => !yaAgregado && handleToggle(equipo)}
                                                    style={{ 
                                                        cursor: yaAgregado ? 'not-allowed' : 'pointer',
                                                        opacity: yaAgregado ? 0.5 : 1 
                                                    }}
                                                    className={isSelected(equipo._id) ? 'table-active' : ''}
                                                >
                                                    <td>{index + 1}</td>
                                                    <td style={{ textAlign: 'center', verticalAlign: 'middle' }} onClick={(e) => e.stopPropagation()}>
                                                        <input type='checkbox'
                                                            checked={isSelected(equipo._id)}
                                                            onChange={() => !yaAgregado && handleToggle(equipo)}
                                                            disabled={yaAgregado}
                                                            style={{ transform: 'scale(1.3)' }}
                                                        />
                                                    </td>
                                                    <td>
                                                        {equipo.item?.nombre || 'N/A'}
                                                        {yaAgregado && <span className="badge bg-secondary ms-2">Ya agregado</span>}
                                                        {equipo.equipoDependiente && (
                                                            <span
                                                                className="badge bg-info ms-2"
                                                                title={`Depende de un equipo padre`}
                                                            >
                                                                🔗 dependiente
                                                            </span>
                                                        )}
                                                        {idsPadres.has(String(equipo._id)) && (
                                                            <span
                                                                className="badge bg-warning text-dark ms-2"
                                                                title="Este equipo tiene dependientes asociados"
                                                            >
                                                                🔗 con dependientes
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td>{equipo.marca || 'N/A'}</td>
                                                    <td>{equipo.modelo || 'N/A'}</td>
                                                    <td>{equipo.serie}</td>
                                                    <td>{equipo.inventario || 'N/A'}</td>
                                                    <td>{equipo.sedeClient?.nameSede || 'N/A'}</td>
                                                    <td>{equipo.ubicacion || 'N/A'}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>

                            <div className="d-grid mt-3">
                                <Button
                                    variant="primary"
                                    onClick={handleAgregar}
                                    disabled={selectedEquipos.length === 0}
                                >
                                    Agregar Seleccionados ({selectedEquipos.length})
                                </Button>
                            </div>
                        </>
                    )}
                </>
            )}

            {/* Modal para corregir equipos con datos incompletos */}
            <ModalCorregirEquipos
                show={showModalCorregir}
                equiposProblematicos={equiposProblematicos}
                listaSedes={listaSedes}
                onCorregir={(equipoId, datosActualizados) => {
                    // Guardar en cache local para usar al cerrar el modal
                    setEquiposCorregidosCache(prev => {
                        const nuevoCache = new Map(prev);
                        nuevoCache.set(equipoId, datosActualizados);
                        return nuevoCache;
                    });
                    // Llamar al handler del padre también
                    onCorregir(equipoId, datosActualizados);
                }}
                onClose={handleModalClose}
                onCancelar={handleModalCancelar}
            />
        </div>
    );
};

export default EquipoExistente;
